import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  MessageCircle, Search, Send, Image, Smile, X, 
  ChevronLeft, Users, Plus, Check, CheckCheck,
  Loader2, User, Paperclip
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import ChatSafetyActions from "../components/community/ChatSafetyActions";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emoji picker data
const EMOJI_CATEGORIES = {
  "자주 쓰는": ["😊", "😂", "❤️", "👍", "🙏", "😍", "🥺", "😭", "🤣", "✨"],
  "표정": ["😀", "😃", "😄", "😁", "😅", "🤪", "😜", "😎", "🥳", "😇"],
  "하트": ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💕", "💖"],
  "손": ["👋", "🤚", "✋", "🖐️", "👌", "🤌", "🤏", "✌️", "🤞", "🫶"],
  "사물": ["🎉", "🎊", "🎁", "🔥", "⭐", "🌟", "💫", "🌈", "☀️", "🌙"]
};

export const ChatPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isAuthenticated } = useAuth();
  
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Auto-start chat with user from URL params
  useEffect(() => {
    const userId = searchParams.get("userId");
    const userName = searchParams.get("userName");
    if (userId && isAuthenticated) {
      startDirectChat(userId, userName);
    }
  }, [searchParams, isAuthenticated]);

  // Fetch chat rooms
  const fetchRooms = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/chat/rooms`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRooms(data);
      }
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchRooms();
    } else {
      const timer = setTimeout(() => {
        if (!isAuthenticated) navigate("/");
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, navigate, fetchRooms]);

  // Fetch messages for selected room
  const fetchMessages = useCallback(async (roomId) => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/chat/rooms/${roomId}/messages`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  }, [token]);

  // WebSocket connection
  useEffect(() => {
    if (!selectedRoom || !token) return;

    const wsUrl = API_URL.replace("http", "ws");
    const ws = new WebSocket(`${wsUrl}/api/ws/chat/${selectedRoom.id}?token=${token}`);
    
    ws.onopen = () => {
      setWsConnected(true);
      // Mark messages as read
      ws.send(JSON.stringify({ type: "read" }));
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "new_message") {
        setMessages(prev => [...prev, data.message]);
        // Mark as read immediately
        ws.send(JSON.stringify({ type: "read" }));
        // Update room list
        fetchRooms();
      } else if (data.type === "typing") {
        setTypingUsers(prev => {
          if (!prev.includes(data.user_name)) {
            return [...prev, data.user_name];
          }
          return prev;
        });
        // Clear typing indicator after 3 seconds
        setTimeout(() => {
          setTypingUsers(prev => prev.filter(u => u !== data.user_name));
        }, 3000);
      } else if (data.type === "messages_read") {
        // Update read status in messages
        setMessages(prev => prev.map(msg => ({
          ...msg,
          read_by: msg.read_by.includes(data.user_id) 
            ? msg.read_by 
            : [...msg.read_by, data.user_id]
        })));
      }
    };
    
    ws.onclose = () => {
      setWsConnected(false);
    };
    
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setWsConnected(false);
    };
    
    wsRef.current = ws;
    
    return () => {
      ws.close();
    };
  }, [selectedRoom, token, fetchRooms]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Select room
  const handleSelectRoom = async (room) => {
    setSelectedRoom(room);
    await fetchMessages(room.id);
    // Mark as read
    try {
      await fetch(`${API_URL}/api/chat/rooms/${room.id}/read`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${token}` }
      });
      fetchRooms(); // Refresh unread counts
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  // Start direct chat
  const startDirectChat = async (userId, userName) => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/chat/rooms/direct`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId })
      });
      if (response.ok) {
        const room = await response.json();
        await fetchRooms();
        setSelectedRoom(room);
        await fetchMessages(room.id);
        setShowNewChatModal(false);
      }
    } catch (error) {
      console.error("Failed to create chat:", error);
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedRoom || isSending) return;
    
    setIsSending(true);
    
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "message",
          content: newMessage,
          message_type: "text"
        }));
        setNewMessage("");
      } else {
        // Fallback to REST API
        const response = await fetch(`${API_URL}/api/chat/rooms/${selectedRoom.id}/messages`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            content: newMessage,
            message_type: "text"
          })
        });
        if (response.ok) {
          const msg = await response.json();
          setMessages(prev => [...prev, msg]);
          setNewMessage("");
          fetchRooms();
        }
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsSending(false);
    }
  };

  // Send typing indicator
  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      wsRef.current.send(JSON.stringify({ type: "typing" }));
      typingTimeoutRef.current = setTimeout(() => {}, 3000);
    }
  };

  // Send emoji
  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Upload image
  // Media upload (image or video)
  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRoom) return;
    
    // Check file type
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    
    if (!isImage && !isVideo) {
      alert("이미지 또는 동영상 파일만 업로드할 수 있습니다.");
      return;
    }
    
    // Check file size (50MB max for video, 10MB for image)
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(isVideo ? "동영상은 50MB 이하만 업로드 가능합니다." : "이미지는 10MB 이하만 업로드 가능합니다.");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      setIsSending(true);
      console.log("Uploading media file...");
      
      const uploadResponse = await fetch(`${API_URL}/api/chat/upload-media`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });
      
      console.log("Upload response status:", uploadResponse.status);
      
      if (uploadResponse.ok) {
        const { url, type } = await uploadResponse.json();
        console.log("Upload successful:", url, type);
        
        const messageType = type === "video" ? "video" : "image";
        const content = type === "video" ? "동영상" : "이미지";
        
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          console.log("Sending via WebSocket");
          wsRef.current.send(JSON.stringify({
            type: "message",
            content: content,
            message_type: messageType,
            image_url: url
          }));
        } else {
          console.log("WebSocket not connected, using REST API fallback");
          const msgResponse = await fetch(`${API_URL}/api/chat/rooms/${selectedRoom.id}/messages`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              content: content,
              message_type: messageType,
              image_url: url
            })
          });
          
          if (msgResponse.ok) {
            const newMsg = await msgResponse.json();
            console.log("Message sent via REST:", newMsg);
            setMessages(prev => [...prev, newMsg]);
          } else {
            console.error("Failed to send message via REST");
            alert("메시지 전송에 실패했습니다.");
          }
        }
        fetchRooms();
      } else {
        const errorText = await uploadResponse.text();
        console.error("Upload failed:", errorText);
        alert("파일 업로드에 실패했습니다.");
      }
    } catch (error) {
      console.error("Failed to upload media:", error);
      alert("파일 업로드 중 오류가 발생했습니다.");
    } finally {
      setIsSending(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Search users
  const handleSearchUsers = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/chat/users/search?q=${encodeURIComponent(query)}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error("Failed to search users:", error);
    } finally {
      setIsSearching(false);
    }
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
    } else if (diffDays === 1) {
      return "어제";
    } else if (diffDays < 7) {
      return ["일", "월", "화", "수", "목", "금", "토"][date.getDay()] + "요일";
    } else {
      return date.toLocaleDateString("ko-KR", { month: "short", day: "numeric" });
    }
  };

  // Get room display name
  const getRoomName = (room) => {
    if (room.is_group) return room.name || "그룹 채팅";
    return room.other_user?.username || room.participant_names?.find(n => n !== user?.username) || "알 수 없음";
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-stone-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-16">
      <div className="max-w-6xl mx-auto h-[calc(100vh-64px)]">
        <div className="flex h-full bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Rooms List */}
          <div className={`w-full md:w-80 border-r border-stone-200 flex flex-col ${selectedRoom ? 'hidden md:flex' : 'flex'}`}>
            {/* Header */}
            <div className="p-4 border-b border-stone-200">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                  <MessageCircle className="w-6 h-6 text-stone-700" />
                  채팅
                </h1>
                <Button
                  size="sm"
                  onClick={() => setShowNewChatModal(true)}
                  className="bg-stone-600 hover:bg-stone-700 text-white"
                  data-testid="new-chat-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  placeholder="채팅방 검색..."
                  className="w-full pl-10 pr-4 py-2 bg-stone-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stone-300"
                />
              </div>
            </div>
            
            {/* Rooms */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-stone-500" />
                </div>
              ) : rooms.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <MessageCircle className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">아직 채팅이 없습니다</p>
                  <p className="text-sm text-stone-400 mt-1">새 채팅을 시작해보세요</p>
                </div>
              ) : (
                rooms.map(room => (
                  <div
                    key={room.id}
                    onClick={() => handleSelectRoom(room)}
                    className={`p-4 border-b border-stone-100 cursor-pointer hover:bg-stone-50 transition-colors ${
                      selectedRoom?.id === room.id ? 'bg-stone-100' : ''
                    }`}
                    data-testid={`chat-room-${room.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        room.is_group ? 'bg-stone-200' : 'bg-stone-200'
                      }`}>
                        {room.is_group ? (
                          <Users className="w-6 h-6 text-stone-600" />
                        ) : (
                          <User className="w-6 h-6 text-stone-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-stone-800 truncate">
                            {getRoomName(room)}
                          </h3>
                          {room.last_message_at && (
                            <span className="text-xs text-stone-400">
                              {formatTime(room.last_message_at)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-sm text-stone-500 truncate">
                            {room.last_sender_name && `${room.last_sender_name}: `}
                            {room.last_message || "메시지 없음"}
                          </p>
                          {room.unread_count > 0 && (
                            <span className="bg-stone-600 text-white text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center">
                              {room.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${!selectedRoom ? 'hidden md:flex' : 'flex'}`}>
            {selectedRoom ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-stone-200 flex items-center gap-3">
                  <button
                    onClick={() => setSelectedRoom(null)}
                    className="md:hidden p-2 hover:bg-stone-100 rounded-lg"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedRoom.is_group ? 'bg-stone-200' : 'bg-stone-200'
                  }`}>
                    {selectedRoom.is_group ? (
                      <Users className="w-5 h-5 text-stone-600" />
                    ) : (
                      <User className="w-5 h-5 text-stone-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h2 className="font-semibold text-stone-800">{getRoomName(selectedRoom)}</h2>
                    <p className="text-xs text-stone-500">
                      {wsConnected ? (
                        <span className="text-green-500">연결됨</span>
                      ) : (
                        <span className="text-stone-400">연결 중...</span>
                      )}
                      {typingUsers.length > 0 && (
                        <span className="ml-2 text-stone-600">
                          {typingUsers.join(", ")} 입력 중...
                        </span>
                      )}
                    </p>
                  </div>
                  {!selectedRoom.is_group && (
                    <ChatSafetyActions
                      otherId={selectedRoom.other_user?.id || selectedRoom.participant_ids?.find((p) => p !== user?.id)}
                      otherName={getRoomName(selectedRoom)}
                      roomId={selectedRoom.id}
                      size="sm"
                    />
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50">
                  {messages.map((msg, idx) => {
                    const isMe = msg.sender_id === user?.id;
                    const showDate = idx === 0 || 
                      new Date(messages[idx-1].created_at).toDateString() !== new Date(msg.created_at).toDateString();
                    
                    return (
                      <div key={msg.id}>
                        {showDate && (
                          <div className="text-center my-4">
                            <span className="bg-stone-200 text-stone-600 text-xs px-3 py-1 rounded-full">
                              {new Date(msg.created_at).toLocaleDateString("ko-KR", {
                                year: "numeric", month: "long", day: "numeric"
                              })}
                            </span>
                          </div>
                        )}
                        <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] ${isMe ? 'order-2' : ''}`}>
                            {!isMe && (
                              <p className="text-xs text-stone-500 mb-1 ml-1">{msg.sender_name}</p>
                            )}
                            <div className={`flex items-end gap-2 ${isMe ? 'flex-row-reverse' : ''}`}>
                              <div className={`rounded-2xl px-4 py-2 ${
                                isMe 
                                  ? 'bg-stone-600 text-white rounded-br-sm' 
                                  : 'bg-white text-stone-800 rounded-bl-sm shadow-sm'
                              }`}>
                                {msg.message_type === "image" ? (
                                  <img 
                                    src={`${API_URL}${msg.image_url}`} 
                                    alt="이미지" 
                                    className="max-w-[200px] rounded-lg cursor-pointer"
                                    onClick={() => window.open(`${API_URL}${msg.image_url}`, '_blank')}
                                  />
                                ) : msg.message_type === "video" ? (
                                  <div className="relative">
                                    <video 
                                      src={`${API_URL}${msg.image_url}`}
                                      className="max-w-[280px] rounded-lg"
                                      controls
                                      preload="metadata"
                                    />
                                  </div>
                                ) : (
                                  <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="text-xs text-stone-400">
                                  {formatTime(msg.created_at)}
                                </span>
                                {isMe && (
                                  <span className="text-xs">
                                    {msg.read_by && msg.read_by.length > 1 ? (
                                      <CheckCheck className="w-3 h-3 text-blue-500" />
                                    ) : (
                                      <Check className="w-3 h-3 text-stone-400" />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t border-stone-200 bg-white">
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                      title="사진/동영상 전송"
                    >
                      <Paperclip className="w-5 h-5 text-stone-500" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleMediaUpload}
                      className="hidden"
                    />
                    
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                      >
                        <Smile className="w-5 h-5 text-stone-500" />
                      </button>
                      
                      {/* Emoji Picker */}
                      {showEmojiPicker && (
                        <div className="absolute bottom-12 left-0 bg-white rounded-xl shadow-xl border border-stone-200 p-3 w-72 z-10">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-stone-700">이모티콘</span>
                            <button onClick={() => setShowEmojiPicker(false)}>
                              <X className="w-4 h-4 text-stone-400" />
                            </button>
                          </div>
                          <div className="space-y-3 max-h-60 overflow-y-auto">
                            {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                              <div key={category}>
                                <p className="text-xs text-stone-500 mb-1">{category}</p>
                                <div className="flex flex-wrap gap-1">
                                  {emojis.map(emoji => (
                                    <button
                                      key={emoji}
                                      type="button"
                                      onClick={() => handleEmojiSelect(emoji)}
                                      className="w-8 h-8 flex items-center justify-center hover:bg-stone-100 rounded text-lg"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      placeholder="메시지를 입력하세요..."
                      className="flex-1 px-4 py-2 bg-stone-100 rounded-full focus:outline-none focus:ring-2 focus:ring-stone-300"
                      data-testid="chat-message-input"
                    />
                    
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || isSending}
                      className="rounded-full w-10 h-10 p-0 bg-stone-600 hover:bg-stone-700 text-white"
                      data-testid="send-message-btn"
                    >
                      {isSending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-stone-50">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-stone-600 mb-2">채팅을 선택하세요</h2>
                  <p className="text-stone-400">대화를 시작하려면 채팅방을 선택하거나<br />새 채팅을 시작하세요</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-stone-800">새 채팅</h2>
              <button onClick={() => setShowNewChatModal(false)}>
                <X className="w-6 h-6 text-stone-400" />
              </button>
            </div>
            
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchUsers(e.target.value)}
                placeholder="사용자 검색..."
                className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-300"
                data-testid="search-user-input"
              />
            </div>
            
            <div className="max-h-80 overflow-y-auto">
              {isSearching ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-stone-500" />
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map(u => (
                  <div
                    key={u.id}
                    onClick={() => startDirectChat(u.id, u.username)}
                    className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg cursor-pointer"
                    data-testid={`user-result-${u.id}`}
                  >
                    <div className="w-10 h-10 bg-stone-200 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-stone-500" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">{u.username}</p>
                      <p className="text-sm text-stone-500">{u.location || "위치 미설정"}</p>
                    </div>
                  </div>
                ))
              ) : searchQuery ? (
                <p className="text-center text-stone-500 py-8">검색 결과가 없습니다</p>
              ) : (
                <p className="text-center text-stone-400 py-8">사용자 이름을 검색하세요</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
